import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { WaypointSharesApi, type WaypointSharePreview } from '@/shared/api/waypointShares'
import { useAuth } from '@/modules/auth'
import { Button, Card, CardContent, Skeleton, Logo } from '@/ui/components'
import { AlertCircle, MapPin, Check } from '@/ui/icons'
import { AppHandoffBanner } from '@/components/AppHandoffBanner'

type Status = 'loading' | 'ready' | 'accepting' | 'accepted' | 'declined' | 'error'

/**
 * Public landing page for an onX-style waypoint share link
 * (/share/waypoint/:token). Shows the waypoint on a map with an Accept/Decline
 * card. Accepting adds it to the recipient's map + library (live-linked) and
 * requires an account — unauthenticated users are routed to login and resume
 * here afterward (RoutePersistence restores the path).
 */
export default function WaypointShareView() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { getAccessToken, isAuthenticated } = useAuth()

  const [status, setStatus] = useState<Status>('loading')
  const [preview, setPreview] = useState<WaypointSharePreview | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mapContainer = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)

  const api = useMemo(() => new WaypointSharesApi(getAccessToken), [getAccessToken])

  // Load the preview.
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!token) {
        setError('Invalid share link')
        setStatus('error')
        return
      }
      try {
        const data = await api.getPreview(token)
        if (cancelled) return
        setPreview(data)
        setStatus('ready')
      } catch {
        if (cancelled) return
        setError('This share link is invalid, expired, or has been revoked.')
        setStatus('error')
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [token, api])

  // Initialize the map once we have coordinates.
  useEffect(() => {
    const wp = preview?.waypoint
    if (!mapContainer.current || mapRef.current || !wp) return
    if (wp.latitude == null || wp.longitude == null) return

    const mapboxToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN
    if (!mapboxToken) return
    mapboxgl.accessToken = mapboxToken

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: [wp.longitude, wp.latitude],
      zoom: 12,
      attributionControl: false,
    })
    new mapboxgl.Marker({ color: '#EA580C' }).setLngLat([wp.longitude, wp.latitude]).addTo(map)
    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [preview])

  async function handleAccept() {
    if (!token) return
    if (!isAuthenticated) {
      // Persist handled by RoutePersistence; return here after login.
      navigate('/login')
      return
    }
    setStatus('accepting')
    try {
      await api.accept(token)
      setStatus('accepted')
    } catch {
      setError('We could not add this waypoint. Please try again.')
      setStatus('error')
    }
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-earth-50">
        <Skeleton className="h-full min-h-screen w-full rounded-none" />
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-earth-50 px-4 py-10">
        <Card className="w-full max-w-md rounded-2xl shadow-lg">
          <CardContent className="flex flex-col items-center p-6 text-center sm:p-8">
            <Logo size="sm" to="/" className="mb-6" />
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="mb-2 text-xl font-bold text-foreground sm:text-2xl">Link unavailable</h1>
            <p className="mb-6 text-sm text-muted-foreground">{error}</p>
            <Button onClick={() => navigate('/')} className="w-full sm:w-auto">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const wp = preview?.waypoint
  const owner = preview?.ownerDisplayName ?? 'A Friend'

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-earth-100">
      {token && <AppHandoffBanner token={token} />}

      {/* Map backdrop */}
      <div ref={mapContainer} className="absolute inset-0 h-full w-full" />
      <div className="absolute inset-0 bg-black/25" />

      {/* Card */}
      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <Card className="w-full max-w-md rounded-2xl bg-card shadow-2xl">
          <CardContent className="flex flex-col items-center p-6 text-center sm:p-8">
            {status === 'accepted' ? (
              <>
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <Check className="h-8 w-8 text-primary" />
                </div>
                <h1 className="mb-2 text-xl font-bold text-foreground sm:text-2xl">
                  Added to your map
                </h1>
                <p className="mb-6 text-sm text-muted-foreground">
                  "{wp?.name}" is now in My Content. You'll automatically see any updates {owner}{' '}
                  makes.
                </p>
                <Button onClick={() => navigate('/dashboard')} className="w-full">
                  Open my map
                </Button>
              </>
            ) : status === 'declined' ? (
              <>
                <h1 className="mb-2 text-xl font-bold text-foreground sm:text-2xl">No problem</h1>
                <p className="mb-6 text-sm text-muted-foreground">
                  This waypoint wasn't added to your map.
                </p>
                <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                  Go Home
                </Button>
              </>
            ) : (
              <>
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                  <MapPin className="h-8 w-8 text-muted-foreground" />
                </div>
                <h1 className="mb-4 text-xl font-bold leading-snug text-foreground sm:text-2xl">
                  {owner} wants to share "{wp?.name}" with you
                </h1>
                <p className="mb-6 text-sm text-muted-foreground">
                  Accepting will add the item to your map and to My Content. You'll automatically see
                  any updates {owner} makes.
                </p>
                <Button
                  onClick={handleAccept}
                  disabled={status === 'accepting'}
                  className="mb-2 w-full"
                >
                  {status === 'accepting' ? 'Adding…' : 'Accept'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setStatus('declined')}
                  className="w-full"
                >
                  Decline
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
